import { useLanguage } from '../contexts/LanguageContext';
import {
  PERMISSION_GROUPS,
  PROFILE_PRESETS,
  requiredBy,
  setPermission,
  type PermissionKey,
  type PermissionSet,
} from '../lib/permissions';

const LABEL_KEYS = Object.fromEntries(
  PERMISSION_GROUPS.flatMap(g => g.items.map(i => [i.key, i.labelKey]))
) as Record<PermissionKey, string>;

/** One switch per permission, grouped by area. Dependencies follow along. */
export function PermissionEditor({
  value,
  onChange,
}: {
  value: PermissionSet;
  onChange: (next: PermissionSet) => void;
}) {
  const { t } = useLanguage();

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
      {PERMISSION_GROUPS.map(group => (
        <fieldset key={group.labelKey} className="rounded-lg border border-gray-200 bg-white p-3">
          <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-gray-500">{t(group.labelKey)}</legend>
          <div className="space-y-2">
            {group.items.map(item => {
              const neededBy = value[item.key] ? requiredBy(value, item.key) : null;
              return (
                <label
                  key={item.key}
                  className="flex items-start gap-2 cursor-pointer"
                  title={neededBy ? t('admin.permRequiredBy').replace('{0}', t(LABEL_KEYS[neededBy])) : undefined}
                >
                  <input
                    type="checkbox"
                    checked={value[item.key]}
                    onChange={(e) => onChange(setPermission(value, item.key, e.target.checked))}
                    className="mt-0.5 w-4 h-4 text-accent-600 rounded flex-shrink-0"
                  />
                  <span className="text-sm text-gray-700 leading-snug">
                    {t(item.labelKey)}
                    {item.hintKey && <span className="block text-xs text-gray-400">{t(item.hintKey)}</span>}
                  </span>
                </label>
              );
            })}
          </div>
        </fieldset>
      ))}
    </div>
  );
}

const BASELINE = PROFILE_PRESETS.agent;

/**
 * Short read-only summary for the users table: what someone has beyond the
 * Agent profile, and (struck through) what they lack from it.
 */
export function PermissionSummary({ value, emptyLabel }: { value: PermissionSet; emptyLabel: string }) {
  const { t } = useLanguage();
  const items = PERMISSION_GROUPS.flatMap(g => g.items);
  const extra = items.filter(i => value[i.key] && !BASELINE[i.key]);
  const missing = items.filter(i => !value[i.key] && BASELINE[i.key]);

  if (extra.length === 0 && missing.length === 0) {
    return <span className="px-2 py-1 text-xs bg-gray-50 text-gray-400 rounded">{emptyLabel}</span>;
  }
  return (
    <div className="flex flex-wrap gap-1">
      {extra.map(i => (
        <span key={i.key} className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded">{t(i.labelKey)}</span>
      ))}
      {missing.map(i => (
        <span key={i.key} className="px-2 py-1 text-xs bg-gray-50 text-gray-400 line-through rounded">{t(i.labelKey)}</span>
      ))}
    </div>
  );
}
